// A loud two-beep alert for new finance requests, synthesised via Web Audio
// (no asset file). Best-effort: any failure — missing AudioContext, autoplay
// block — is swallowed so audio never disrupts the stream handler.
//
// Background-tab playback: a WebSocket message is still delivered when the
// platform tab is backgrounded, but an AudioContext only produces sound once
// it is `running`, which needs a prior user gesture. We therefore unlock the
// context on the operator's first interaction (below), so it is already
// `running` by the time they switch to another tab and a beep must fire from a
// non-gesture WS callback.
//
// ponytail: no mute toggle / persistence. Add a localStorage-backed mute when
// operators ask; the player app's sound.ts has the pattern.

// Peak amplitude of each beep (0..1). Bumped from 0.2 so it carries from a
// backgrounded tab.
const PEAK = 0.75;

let ctx: AudioContext | null = null;

function getContext(): AudioContext | null {
  try {
    if (!ctx) {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      ctx = new Ctor();
    }
    return ctx;
  } catch {
    return null;
  }
}

function resumeIfSuspended(audio: AudioContext): void {
  if (audio.state === 'suspended') void audio.resume();
}

// One rising tone with a fade in/out envelope, scheduled at `at` seconds.
function tone(audio: AudioContext, at: number, f0: number, f1: number, dur: number): void {
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(f0, at);
  osc.frequency.exponentialRampToValueAtTime(f1, at + dur * 0.7);
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(PEAK, at + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  osc.connect(gain).connect(audio.destination);
  osc.start(at);
  osc.stop(at + dur + 0.02);
}

// Unlock the context on the first user gesture (persistent, cheap: resume() is
// a no-op once running). Keeps it `running` for background WS beeps.
if (typeof window !== 'undefined') {
  const unlock = (): void => {
    const audio = getContext();
    if (audio) resumeIfSuspended(audio);
  };
  window.addEventListener('pointerdown', unlock, { passive: true });
  window.addEventListener('keydown', unlock, { passive: true });
}

function emit(audio: AudioContext): void {
  const t0 = audio.currentTime;
  tone(audio, t0, 880, 1320, 0.18);
  tone(audio, t0 + 0.22, 990, 1480, 0.2);
}

export function playNotifySound(): void {
  try {
    const audio = getContext();
    if (!audio) return;
    if (audio.state === 'running') {
      emit(audio);
    } else {
      // Never gesture-unlocked, or browser-suspended the context: resume and
      // emit once running, scheduled against a fresh currentTime. Avoids
      // dropping a first-ever beep that fires while the tab is backgrounded.
      void audio
        .resume()
        .then(() => {
          if (audio.state === 'running') emit(audio);
        })
        .catch(() => {});
    }
  } catch {
    // ignore — audio is a nicety, never a hard dependency
  }
}
